/**
 * Monte Carlo Evaluator for Intermediate AI
 *
 * Evaluates candidate plays by simulating many games forward
 * with sampled opponent hands and beginner AI rollout.
 *
 * Key insight: We evaluate TEAM outcome, not individual trick wins.
 * Partnership dynamics emerge naturally from the simulation.
 */

import type { Bid, GameAction, GameState } from '../types';
import type { ValidAction } from '../../multiplayer/types';
import { actionToId } from '../core/actions';
import type { HandConstraints } from './constraint-tracker';
import type { SampledHands } from './hand-sampler';
import {
  sampleOpponentHands,
  sampleBiddingHands,
  createSeededRng,
  type RandomGenerator,
} from './hand-sampler';
import {
  getExpectedHandSizes,
  buildCanFollowCache,
} from './constraint-tracker';
import { executeAction, type ExecuteActionOptions } from '../core/actions';
import type { ExecutionContext } from '../types/execution';
import { createDominoes } from '../core/dominoes';
import { getWinningTeam } from '../core/scoring';
import { selectDeclarationAction } from './hand-strength';
import {
  minimaxEvaluate,
  createTerminalState,
  orderMovesForSearch,
} from './minimax';
import { enumerateExactEndgameWorlds } from './exact-endgame';
import type { BehaviorModel } from './behavior-model.js';
import { HUMAN_PLAYER_INDEX } from './behavior-model.js';
import { collectHumanPublicActions } from './human-action-observations.js';
import { scoreCandidateHumanHandLikelihood } from './adaptive-learning.js';
import {
  aggregateCountDestination,
  classifyPartnerSafety,
  prepareContextualPlayFamily,
  type BossPreservation,
  type CountDestination,
  type PartnerSafetyAssessment,
} from './contextual-assessment';
import { buildPairedPlayConfidence } from './paired-confidence';
import type { AIPairedPlayConfidence, AIPlaySearchStopReason } from './types';

/** Reusable options object to avoid allocation per call */
const SIMULATION_OPTIONS: ExecuteActionOptions = { skipHistory: true, skipValidation: true };
/** 28 plays + 7 trick completions + scoring, with headroom for variant automation. */
const MAX_ROLLOUT_ACTIONS = 64;
/** A set contract is scored as the worst possible hand outcome. */
const SET_CONTRACT_UTILITY = -42;

/**
 * Configuration for Monte Carlo evaluation
 */
export interface MonteCarloConfig {
  /** Number of simulations for bidding decisions (default: 5) */
  biddingSimulations: number;

  /** Number of simulations for play decisions (default: 10) */
  playingSimulations: number;

  /** Maximum sampled worlds for confidence-aware play expansion. */
  playingMaximumWorlds: number;

  /** Random seed for reproducibility (optional) */
  seed?: number;
}

export interface BehaviorWeightContext {
  model?: BehaviorModel;
  confidence: number;
  humanPlayerIndex?: number;
}

export interface ProfiledSearchOptions {
  rng?: RandomGenerator;
  matchEquityMultiplier?: number;
  exactEndgameAtTiles?: number | null;
  useBehaviorWeights?: boolean;
  behaviorWeights?: BehaviorWeightContext;
  /** Strategic band that a statistically clear leader must also exceed. */
  strategicTolerance?: number;
  /** Monotonic timestamp after which the search must fail closed. */
  deadlineAt?: number;
}

export class SearchDeadlineExceeded extends Error {
  constructor() {
    super('AI search deadline exceeded');
    this.name = 'SearchDeadlineExceeded';
  }
}

function assertWithinDeadline(deadlineAt?: number): void {
  if (deadlineAt !== undefined && globalThis.performance.now() >= deadlineAt) {
    throw new SearchDeadlineExceeded();
  }
}

/**
 * Result of evaluating a single action
 */
export interface ActionEvaluation {
  /** The action that was evaluated */
  action: ValidAction;

  /** Average team points across simulations */
  avgTeamPoints: number;

  /** Fraction of simulations where the acting team earned the contract marks */
  winRate: number;

  /** Average utility with every failed contract valued at -42 */
  contractUtility: number;

  /** Contract utility for each hidden world, retained in shared world order. */
  contractUtilityOutcomes: number[];

  /** One common bounded weight for each hidden world. */
  worldWeights: number[];

  /** Whether these worlds are sampled or exhaust the valid hidden deals. */
  worldSource: 'sampled' | 'complete-enumeration' | 'truncated-enumeration';

  /** Number of simulations run */
  simulationCount: number;

  /** Contextual partner lead classification across evaluated worlds. */
  partnerSafety: PartnerSafetyAssessment['partnerSafety'];

  /** Share of legal one-trick continuations won by the partner. */
  partnerWinRate: number;

  /** Whether this action overtakes the partner in an evaluated world. */
  overtakesPartner: boolean;

  /** Destination of this action's current-trick points across evaluated worlds. */
  countDestination: CountDestination;

  /** Points released by the candidate domino (public information). */
  countRisk: number;

  /** The candidate is the highest unplayed domino in its own suit (engine suit/trump algebra). */
  liveStanding: boolean;

  /** Whether the candidate spends or keeps a live boss domino. */
  bossPreservation: BossPreservation;

  /** The candidate is a live boss with a lower-ranked same-suit tile unplayed outside the acting hand. */
  likelyFutureLeadValue: boolean;

  /** Whether the candidate wins the completed trick in a strict majority of evaluated worlds. */
  candidateWinsTrick: boolean;

  /** Average retained count value with a plausible promotion route. */
  protectedCountValue: number;
  /** Average share of retained count supported by a promotion route. */
  promotionSupport: number;
  /** Share of sampled worlds with a plausible route to the lead. */
  leadAccess: number;
  /** Share of sampled worlds where the team retains a control route. */
  remainingControl: number;
  /** Share of sampled worlds where the retained count plan is cashable. */
  cashability: number;
  /** Share of sampled worlds where the current trick immediately secures the contract. */
  contractSecuringRate: number;
  /** Average protected count value released by this play. */
  sacrificedProtectedCountValue: number;

  /** Whether a majority of applicable lead worlds retain team trump control. */
  retainedTrumpControl?: boolean;
  /** Whether a majority of applicable lead worlds avoid stripping the partner's final trump. */
  protectsPartnerTrumpControl?: boolean;
  /** Whether a majority of applicable lead worlds force the bidder's final trump. */
  leadPressure?: boolean;
  /** Average public value of the acting seat's delayed trump re-entry plan. */
  reentryPlanValue?: number;
  actingSeatRetainedTrump?: boolean;
  externalVoidRuffOpportunity?: boolean;
  retainedUsefulContinuation?: boolean;
  weakOffCleanup?: boolean;
}

export interface PlayEvaluationResult {
  evaluations: ActionEvaluation[];
  pairedConfidence?: AIPairedPlayConfidence;
  stopReason?: AIPlaySearchStopReason;
}

interface PlayEvaluationAccumulator {
  action: ValidAction;
  totalTeamPoints: number;
  totalContractUtility: number;
  contractUtilityOutcomes: number[];
  worldWeights: number[];
  totalWeight: number;
  wins: number;
  simulationCount: number;
  totalPartnerWinRate: number;
  partnerCurrentInEveryWorld: boolean;
  overtakesPartner: boolean;
  countDestinations: CountDestination[];
  candidateWins: number;
  totalProtectedCountValue: number;
  totalPromotionSupport: number;
  totalLeadAccess: number;
  totalRemainingControl: number;
  totalCashability: number;
  totalContractSecuringRate: number;
  totalSacrificedProtectedCountValue: number;
  retainedTrumpControl: { trueCount: number; applicableCount: number };
  protectsPartnerTrumpControl: { trueCount: number; applicableCount: number };
  leadPressure: { trueCount: number; applicableCount: number };
  totalReentryPlanValue: number;
  actingSeatRetainedTrump: { trueCount: number; applicableCount: number };
  externalVoidRuffOpportunity: { trueCount: number; applicableCount: number };
  retainedUsefulContinuation: { trueCount: number; applicableCount: number };
  weakOffCleanup: { trueCount: number; applicableCount: number };
}

/**
 * Result of evaluating a bid action
 */
export interface BidEvaluation {
  /** The bid action that was evaluated */
  action: ValidAction;

  /** Engine comparison value, including mark and special contracts */
  bidValue: number;

  /** Fraction of simulations where bid was made */
  makeBidRate: number;

  /** Average team points when bid was made */
  avgPointsWhenMade: number;

  /** Average team points overall (including failures) */
  avgPointsOverall: number;

  /** Average terminal match value across paired completed rollouts. */
  estimatedMatchValue: number;

  /** Number of simulations run */
  simulationCount: number;
}

// All 28 dominoes (cached for bidding simulations)
const ALL_DOMINOES = createDominoes();

/**
 * Evaluate all candidate bid actions using Monte Carlo simulation.
 *
 * For each candidate bid:
 * 1. Sample opponent hands (no constraints during bidding)
 * 2. Execute the real bid and remaining auction transitions
 * 3. Let the actual contract selector declare through the shared AI policy
 * 4. Roll out the composed contract to completion
 * 5. Ask the composed rules whether the bidder's team earned marks
 *
 * @param state Current game state (bidding phase)
 * @param bidActions Array of valid bid actions to evaluate
 * @param myPlayerIndex The AI player's index (0-3)
 * @param ctx Execution context with composed rules
 * @param config Monte Carlo configuration
 * @returns Evaluations for each action, sorted by makeBidRate descending
 */
export function evaluateBidActions(
  state: GameState,
  bidActions: ValidAction[],
  myPlayerIndex: number,
  ctx: ExecutionContext,
  config: MonteCarloConfig,
  searchOptions: ProfiledSearchOptions = {},
): BidEvaluation[] {
  assertWithinDeadline(searchOptions.deadlineAt);
  const rng =
    searchOptions.rng ??
    (config.seed !== undefined
      ? createSeededRng(config.seed)
      : { random: () => Math.random() });

  const myTeam = (myPlayerIndex % 2) as 0 | 1;
  const myHand = state.players[myPlayerIndex]?.hand ?? [];
  const candidates = bidActions.flatMap((bidAction) => {
    const bidValue = getBidComparisonValue(bidAction, ctx);
    if (bidValue === 0) return [];
    return [
      {
        action: bidAction,
        bidValue,
        totalPoints: 0,
        totalMatchValue: 0,
        pointsWhenMade: 0,
        madeCount: 0,
        simulationCount: 0,
      },
    ];
  });
  // A bid decision is one bounded search, not N independent searches.
  // Each round samples one opponent distribution and evaluates every candidate
  // against that same world so make rates remain comparable.
  for (let round = 0; round < config.biddingSimulations; round += 1) {
    assertWithinDeadline(searchOptions.deadlineAt);
    const sampledHands = sampleBiddingHands(
      ALL_DOMINOES,
      myHand,
      myPlayerIndex,
      rng,
    );

    for (const candidate of candidates) {
      assertWithinDeadline(searchOptions.deadlineAt);
      const simState = createContractReadyState(
        state,
        sampledHands,
        myPlayerIndex,
        candidate.action.action,
        ctx,
      );
      if (!simState) {
        candidate.simulationCount += 1;
        continue;
      }
      const finalState = rolloutToHandEnd(simState, ctx, false, searchOptions.deadlineAt);
      const teamPoints = finalState.teamScores[myTeam] ?? 0;
      candidate.totalPoints += teamPoints;
      const scoredMarks = ctx.rules.calculateScore(finalState);
      const winner = getWinningTeam(scoredMarks, state.gameTarget);
      const worldValue = winner === myTeam
        ? 1
        : winner !== null
          ? -1
          : ((scoredMarks[myTeam] ?? 0) - (scoredMarks[1 - myTeam] ?? 0)) / state.gameTarget;
      candidate.totalMatchValue += worldValue;
      const contractMade =
        (scoredMarks[myTeam] ?? 0) > (state.teamMarks[myTeam] ?? 0);

      if (contractMade) {
        candidate.madeCount += 1;
        candidate.pointsWhenMade += teamPoints;
      }
      candidate.simulationCount += 1;
    }
  }

  return toBidEvaluations(candidates);
}

function toBidEvaluations(
  candidates: Array<{
    action: ValidAction;
    bidValue: number;
    totalPoints: number;
    totalMatchValue: number;
    pointsWhenMade: number;
    madeCount: number;
    simulationCount: number;
  }>,
): BidEvaluation[] {
  const evaluations = candidates.map((candidate) => ({
    action: candidate.action,
    bidValue: candidate.bidValue,
    makeBidRate:
      candidate.simulationCount > 0
        ? candidate.madeCount / candidate.simulationCount
        : 0,
    avgPointsWhenMade:
      candidate.madeCount > 0
        ? candidate.pointsWhenMade / candidate.madeCount
        : 0,
    avgPointsOverall:
      candidate.simulationCount > 0
        ? candidate.totalPoints / candidate.simulationCount
        : 0,
    estimatedMatchValue:
      candidate.simulationCount > 0
        ? candidate.totalMatchValue / candidate.simulationCount
        : 0,
    simulationCount: candidate.simulationCount,
  }));
  evaluations.sort((a, b) => b.makeBidRate - a.makeBidRate);
  return evaluations;
}

/**
 * Get the engine's ordering value for a bid action.
 */
function getBidComparisonValue(
  action: ValidAction,
  ctx: ExecutionContext,
): number {
  if (action.action.type !== 'bid') return 0;
  const bid: Bid = {
    type: action.action.bid,
    player: action.action.player,
    ...(action.action.value !== undefined
      ? { value: action.action.value }
      : {}),
  };
  return ctx.rules.getBidComparisonValue(bid);
}

/**
 * Apply a candidate through the real auction and declaration transitions.
 * Remaining bidders pass, then the composed rules choose the declaration
 * actor and first leader. Splash and Plunge therefore use the partner's
 * sampled hand rather than the bidder's hand.
 */
function createContractReadyState(
  state: GameState,
  sampledHands: SampledHands,
  bidderIndex: number,
  bidAction: GameAction,
  ctx: ExecutionContext,
): GameState | null {
  if (bidAction.type !== 'bid' || bidAction.player !== bidderIndex) return null;

  let simulation = injectSampledHands(state, sampledHands, bidderIndex);
  simulation = executeAction(
    simulation,
    bidAction,
    ctx.rules,
    SIMULATION_OPTIONS,
  );

  while (simulation.phase === 'bidding') {
    const pass = ctx
      .getValidActions(simulation)
      .find(
        (action) =>
          action.type === 'pass' && action.player === simulation.currentPlayer,
      );
    if (!pass) return null;
    simulation = executeAction(simulation, pass, ctx.rules, SIMULATION_OPTIONS);
  }

  if (simulation.phase !== 'trump_selection') return null;
  const selector = simulation.currentPlayer;
  const selectorHand = simulation.players[selector]?.hand ?? [];
  const declaration = selectDeclarationAction(
    selectorHand,
    ctx.getValidActions(simulation),
    { bid: simulation.currentBid },
  );
  if (!declaration) return null;

  return executeAction(simulation, declaration, ctx.rules, SIMULATION_OPTIONS);
}

/**
 * Evaluate all candidate play actions using Monte Carlo simulation.
 *
 * For one ordered hidden-world set:
 * 1. Sample opponent hands once (respecting constraints)
 * 2. Apply every candidate action to that same world
 * 3. Rollout to hand completion using beginner AI
 * 4. Record each candidate's team outcome
 *
 * @param state Current game state (with hidden opponent hands)
 * @param playActions Array of valid play actions to evaluate
 * @param myPlayerIndex The AI player's index (0-3)
 * @param constraints Constraints on opponent hands
 * @param ctx Execution context with composed rules
 * @param config Monte Carlo configuration
 * @returns Evaluations for each action, sorted by contract utility descending
 */
export function evaluatePlayActions(
  state: GameState,
  playActions: ValidAction[],
  myPlayerIndex: number,
  constraints: HandConstraints,
  ctx: ExecutionContext,
  config: MonteCarloConfig,
  searchOptions: ProfiledSearchOptions = {},
): PlayEvaluationResult {
  assertWithinDeadline(searchOptions.deadlineAt);
  if (playActions.length === 0) return { evaluations: [] };
  const rng =
    searchOptions.rng ??
    (config.seed !== undefined
      ? createSeededRng(config.seed)
      : { random: () => Math.random() });

  const myTeam = (myPlayerIndex % 2) as 0 | 1;
  const expectedSizes = getExpectedHandSizes(state);
  const canFollowCache = buildCanFollowCache(state, ctx.rules);
  const equityMultiplier = searchOptions.matchEquityMultiplier ?? 1;
  const enumeratedWorldSet = enumerateExactEndgameWorlds(
    state,
    constraints,
    ctx.rules,
    searchOptions.exactEndgameAtTiles ?? null,
  );
  const enumeratedWorlds = enumeratedWorldSet?.worlds ?? [];

  const behaviorContext =
    searchOptions.useBehaviorWeights && searchOptions.behaviorWeights
      ? searchOptions.behaviorWeights
      : undefined;
  const humanPlayerIndex =
    behaviorContext?.humanPlayerIndex ?? HUMAN_PLAYER_INDEX;
  const publicActions =
    behaviorContext !== undefined
      ? collectHumanPublicActions(state, humanPlayerIndex)
      : [];
  const marksBefore: [number, number] = [
    state.teamMarks[0] ?? 0,
    state.teamMarks[1] ?? 0,
  ];
  const checkBudget = () => assertWithinDeadline(searchOptions.deadlineAt);

  // The future-control fields depend only on played dominoes, trump, and the
  // acting hand, so they are identical across every sampled world. Compute
  // them once per candidate instead of re-aggregating per-world values.
  const contextualFamily = prepareContextualPlayFamily(
    state,
    playActions.map(({ action }) => action),
    ctx,
  );
  const futureControl = contextualFamily.futureControl;

  const accumulators: PlayEvaluationAccumulator[] = playActions.map((action) => ({
    action,
    totalTeamPoints: 0,
    totalContractUtility: 0,
    contractUtilityOutcomes: [],
    worldWeights: [],
    totalWeight: 0,
    wins: 0,
    simulationCount: 0,
    totalPartnerWinRate: 0,
    partnerCurrentInEveryWorld: true,
    overtakesPartner: false,
    countDestinations: [],
    candidateWins: 0,
    totalProtectedCountValue: 0,
    totalPromotionSupport: 0,
    totalLeadAccess: 0,
    totalRemainingControl: 0,
    totalCashability: 0,
    totalContractSecuringRate: 0,
    totalSacrificedProtectedCountValue: 0,
    retainedTrumpControl: { trueCount: 0, applicableCount: 0 },
    protectsPartnerTrumpControl: { trueCount: 0, applicableCount: 0 },
    leadPressure: { trueCount: 0, applicableCount: 0 },
    totalReentryPlanValue: 0,
    actingSeatRetainedTrump: { trueCount: 0, applicableCount: 0 },
    externalVoidRuffOpportunity: { trueCount: 0, applicableCount: 0 },
    retainedUsefulContinuation: { trueCount: 0, applicableCount: 0 },
    weakOffCleanup: { trueCount: 0, applicableCount: 0 },
  }));

  const evaluateWorld = (sampledHands: SampledHands, useExactSearch: boolean): void => {
    assertWithinDeadline(searchOptions.deadlineAt);
    let weight = 1;
    if (behaviorContext?.model && behaviorContext.confidence > 0) {
      const candidateHand = sampledHands.get(humanPlayerIndex);
      if (candidateHand) {
        weight = scoreCandidateHumanHandLikelihood(
          behaviorContext.model,
          behaviorContext.confidence,
          publicActions,
          candidateHand,
          marksBefore,
          humanPlayerIndex % 2,
        );
      }
    }

    const fullState = injectSampledHands(state, sampledHands, myPlayerIndex);
    const contextualAssessments = contextualFamily
      .forState(fullState, checkBudget)
      .materializeAll();
    for (let index = 0; index < accumulators.length; index += 1) {
      const accumulator = accumulators[index]!;
      assertWithinDeadline(searchOptions.deadlineAt);
      const assessment = contextualAssessments[index]!;
      let simState = executeAction(
        fullState,
        accumulator.action.action,
        ctx.rules,
        SIMULATION_OPTIONS,
      );
      simState = rolloutToHandEnd(
        simState,
        ctx,
        useExactSearch,
        searchOptions.deadlineAt,
      );
      const teamPoints = simState.teamScores[myTeam] * equityMultiplier;
      accumulator.totalTeamPoints += teamPoints * weight;
      accumulator.totalWeight += weight;
      const scoredMarks = ctx.rules.calculateScore(simState);
      const contractSucceeded = scoredMarks[myTeam] > marksBefore[myTeam];
      const contractUtility = contractSucceeded ? teamPoints : SET_CONTRACT_UTILITY;
      accumulator.totalContractUtility += contractUtility * weight;
      accumulator.contractUtilityOutcomes.push(contractUtility);
      accumulator.worldWeights.push(weight);
      if (contractSucceeded) {
        accumulator.wins += weight;
      }
      accumulator.totalPartnerWinRate += assessment.partnerWinRate * weight;
      accumulator.partnerCurrentInEveryWorld &&= assessment.partnerCurrent;
      accumulator.overtakesPartner ||= assessment.overtakesPartner;
      accumulator.countDestinations.push(assessment.countDestination);
      if (assessment.candidateWinsTrick) accumulator.candidateWins += 1;
      accumulator.totalProtectedCountValue += assessment.protectedCountValue * weight;
      accumulator.totalPromotionSupport += assessment.promotionSupport * weight;
      accumulator.totalLeadAccess += Number(assessment.leadAccess) * weight;
      accumulator.totalRemainingControl += Number(assessment.remainingControl) * weight;
      accumulator.totalCashability += assessment.cashability * weight;
      accumulator.totalContractSecuringRate += assessment.contractSecuringRate * weight;
      accumulator.totalSacrificedProtectedCountValue +=
        assessment.sacrificedProtectedCountValue * weight;
      recordLeadControl(accumulator.retainedTrumpControl, assessment.retainedTrumpControl);
      recordLeadControl(accumulator.protectsPartnerTrumpControl, assessment.protectsPartnerTrumpControl);
      recordLeadControl(accumulator.leadPressure, assessment.leadPressure);
      accumulator.totalReentryPlanValue += (assessment.reentryPlanValue ?? 0) * weight;
      recordLeadControl(accumulator.actingSeatRetainedTrump, assessment.actingSeatRetainedTrump);
      recordLeadControl(accumulator.externalVoidRuffOpportunity, assessment.externalVoidRuffOpportunity);
      recordLeadControl(accumulator.retainedUsefulContinuation, assessment.retainedUsefulContinuation);
      recordLeadControl(accumulator.weakOffCleanup, assessment.weakOffCleanup);
      accumulator.simulationCount += 1;
    }
  };

  const buildEvaluations = (worldSource: ActionEvaluation['worldSource']): ActionEvaluation[] => {
    const evaluations = accumulators.map((accumulator, index) => ({
      action: accumulator.action,
      avgTeamPoints:
        accumulator.totalWeight > 0
          ? accumulator.totalTeamPoints / accumulator.totalWeight
          : 0,
      winRate:
        accumulator.totalWeight > 0
          ? accumulator.wins / accumulator.totalWeight
          : 0,
      contractUtility:
        accumulator.totalWeight > 0
          ? accumulator.totalContractUtility / accumulator.totalWeight
          : 0,
      contractUtilityOutcomes: accumulator.contractUtilityOutcomes,
      worldWeights: accumulator.worldWeights,
      worldSource,
      simulationCount: accumulator.simulationCount,
      partnerSafety: classifyPartnerSafety(
        accumulator.totalWeight > 0 && accumulator.partnerCurrentInEveryWorld,
        accumulator.totalWeight > 0
          ? accumulator.totalPartnerWinRate / accumulator.totalWeight
          : 0,
      ),
      partnerWinRate:
        accumulator.totalWeight > 0
          ? accumulator.totalPartnerWinRate / accumulator.totalWeight
          : 0,
      overtakesPartner: accumulator.overtakesPartner,
      countDestination: aggregateCountDestination(accumulator.countDestinations),
      countRisk: futureControl[index]!.countRisk,
      liveStanding: futureControl[index]!.liveStanding,
      bossPreservation: futureControl[index]!.bossPreservation,
      likelyFutureLeadValue: futureControl[index]!.likelyFutureLeadValue,
      // Current-trick control is present only when it survives a strict majority
      // of the same hidden worlds used for this action's contract utility.
      candidateWinsTrick:
        accumulator.simulationCount > 0 &&
        accumulator.candidateWins > accumulator.simulationCount / 2,
      protectedCountValue: accumulator.totalWeight > 0
        ? accumulator.totalProtectedCountValue / accumulator.totalWeight
        : 0,
      promotionSupport: accumulator.totalWeight > 0
        ? accumulator.totalPromotionSupport / accumulator.totalWeight
        : 0,
      leadAccess: accumulator.totalWeight > 0
        ? accumulator.totalLeadAccess / accumulator.totalWeight
        : 0,
      remainingControl: accumulator.totalWeight > 0
        ? accumulator.totalRemainingControl / accumulator.totalWeight
        : 0,
      cashability: accumulator.totalWeight > 0
        ? accumulator.totalCashability / accumulator.totalWeight
        : 0,
      contractSecuringRate: accumulator.totalWeight > 0
        ? accumulator.totalContractSecuringRate / accumulator.totalWeight
        : 0,
      sacrificedProtectedCountValue: accumulator.totalWeight > 0
        ? accumulator.totalSacrificedProtectedCountValue / accumulator.totalWeight
        : 0,
      ...(aggregateLeadControl(accumulator.retainedTrumpControl, 'retainedTrumpControl')),
      ...(aggregateLeadControl(accumulator.protectsPartnerTrumpControl, 'protectsPartnerTrumpControl')),
      ...(aggregateLeadControl(accumulator.leadPressure, 'leadPressure')),
      reentryPlanValue: accumulator.totalWeight > 0
        ? accumulator.totalReentryPlanValue / accumulator.totalWeight
        : 0,
      ...(aggregateLeadControl(accumulator.actingSeatRetainedTrump, 'actingSeatRetainedTrump')),
      ...(aggregateLeadControl(accumulator.externalVoidRuffOpportunity, 'externalVoidRuffOpportunity')),
      ...(aggregateLeadControl(accumulator.retainedUsefulContinuation, 'retainedUsefulContinuation')),
      ...(aggregateLeadControl(accumulator.weakOffCleanup, 'weakOffCleanup')),
    }));

    // Candidate order is part of the receipt. Break equal values by semantic
    // action ID before seeded near-equal selection.
    evaluations.sort(
      (left, right) =>
        right.contractUtility - left.contractUtility ||
        compareStableIds(actionToId(left.action.action), actionToId(right.action.action)),
    );
    return evaluations;
  };

  if (enumeratedWorlds.length > 0 && enumeratedWorldSet) {
    for (const sampledHands of enumeratedWorlds) {
      evaluateWorld(sampledHands, true);
    }
    const evaluations = buildEvaluations(
      enumeratedWorldSet.complete ? 'complete-enumeration' : 'truncated-enumeration',
    );
    return {
      evaluations,
      stopReason: enumeratedWorldSet.complete ? 'complete-enumeration' : 'truncated-enumeration',
      ...(evaluations.length > 1
        ? {
            pairedConfidence: buildPairedPlayConfidence(
              evaluations,
              searchOptions.strategicTolerance ?? 0,
            ),
          }
        : {}),
    };
  }

  const sampleTiers = buildPlaySampleTiers(
    config.playingSimulations,
    config.playingMaximumWorlds,
  );
  for (const tier of sampleTiers) {
    while (accumulators[0]!.simulationCount < tier) {
      evaluateWorld(
        samplePlayWorld(
          constraints,
          expectedSizes,
          state,
          ctx,
          rng,
          canFollowCache,
          searchOptions.deadlineAt,
        ),
        false,
      );
    }
    const evaluations = buildEvaluations('sampled');
    const pairedConfidence =
      evaluations.length > 1
        ? buildPairedPlayConfidence(evaluations, searchOptions.strategicTolerance ?? 0)
        : undefined;
    if (pairedConfidence?.clearLeader || tier === sampleTiers[sampleTiers.length - 1]) {
      return {
        evaluations,
        stopReason: pairedConfidence?.clearLeader ? 'clear-leader' : 'profile-maximum',
        ...(pairedConfidence ? { pairedConfidence } : {}),
      };
    }
  }

  return { evaluations: [] };
}

function recordLeadControl(
  accumulator: { trueCount: number; applicableCount: number },
  value: boolean | undefined,
): void {
  if (value === undefined) return;
  accumulator.applicableCount += 1;
  if (value) accumulator.trueCount += 1;
}

function aggregateLeadControl<T extends string>(
  accumulator: { trueCount: number; applicableCount: number },
  field: T,
): Partial<Record<T, boolean>> {
  if (accumulator.applicableCount === 0) return {};
  return {
    [field]: accumulator.trueCount > accumulator.applicableCount / 2,
  } as Partial<Record<T, boolean>>;
}

function compareStableIds(left: string, right: string): number {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

/** Build the deterministic cumulative world counts used by sampled play search. */
export function buildPlaySampleTiers(minimum: number, maximum: number): number[] {
  if (
    !Number.isSafeInteger(minimum) ||
    !Number.isSafeInteger(maximum) ||
    minimum < 0 ||
    maximum < minimum
  ) {
    throw new Error('Play world limits require safe integers with maximum at least minimum.');
  }
  if (minimum === 0) return [];

  const tiers = [minimum];
  while (tiers[tiers.length - 1]! < maximum) {
    tiers.push(Math.min(maximum, tiers[tiers.length - 1]! * 2));
  }
  return tiers;
}

function samplePlayWorld(
  constraints: HandConstraints,
  expectedSizes: [number, number, number, number],
  state: GameState,
  ctx: ExecutionContext,
  rng: RandomGenerator,
  canFollowCache: ReturnType<typeof buildCanFollowCache>,
  deadlineAt?: number,
): SampledHands {
  assertWithinDeadline(deadlineAt);
  try {
    return sampleOpponentHands(
      constraints,
      expectedSizes,
      state,
      ctx.rules,
      rng,
      canFollowCache,
    );
  } catch (error) {
    const debugInfo = {
      phase: state.phase,
      tricksPlayed: state.tricks.length,
      currentTrickSize: state.currentTrick.length,
      myPlayerIndex: constraints.myPlayerIndex,
      expectedSizes,
      playedCount: constraints.played.size,
      myHandSize: constraints.myHand.size,
      voidSuits: Object.fromEntries(
        Array.from(constraints.voidInSuit.entries()).map(
          ([key, value]) => [key, Array.from(value)],
        ),
      ),
    };
    throw new Error(
      `${error instanceof Error ? error.message : error}\n` +
        `Debug info: ${JSON.stringify(debugInfo, null, 2)}`,
    );
  }
}

/**
 * Create a copy of the state with sampled opponent hands injected.
 *
 * The AI's own hand stays unchanged. Opponent hands are replaced
 * with the sampled distributions.
 */
function injectSampledHands(
  state: GameState,
  sampledHands: SampledHands,
  myPlayerIndex: number,
): GameState {
  const newPlayers = state.players.map((player, index) => {
    if (index === myPlayerIndex) {
      // Keep AI's own hand
      return player;
    }

    const sampledHand = sampledHands.get(index);
    if (!sampledHand) {
      // Shouldn't happen, but keep original if missing
      return player;
    }

    return {
      ...player,
      hand: sampledHand,
    };
  });

  return {
    ...state,
    players: newPlayers,
    // Override playerTypes to all AI so consensus layer auto-executes
    // (without this, simulations with human players would stall on agree-trick)
    playerTypes: ['ai', 'ai', 'ai', 'ai'] as const,
  };
}

/**
 * Evaluate a sampled hand to completion.
 *
 * Exact minimax remains the endgame evaluator when the profile's hidden-world
 * enumeration is tractable. Earlier positions use the same rule-aware move
 * ordering as minimax for a bounded full-hand rollout.
 */
function rolloutToHandEnd(
  initialState: GameState,
  ctx: ExecutionContext,
  useExactSearch = false,
  deadlineAt?: number,
): GameState {
  assertWithinDeadline(deadlineAt);
  if (useExactSearch) {
    const result = minimaxEvaluate(initialState, ctx);
    assertWithinDeadline(deadlineAt);
    return createTerminalState(initialState, result);
  }

  let state = initialState;
  const checkBudget = () => assertWithinDeadline(deadlineAt);

  for (
    let actionsExecuted = 0;
    actionsExecuted < MAX_ROLLOUT_ACTIONS;
    actionsExecuted += 1
  ) {
    assertWithinDeadline(deadlineAt);
    if (
      state.phase === 'scoring' ||
      state.phase === 'game_end' ||
      state.phase === 'one-hand-complete'
    ) {
      return state;
    }

    const actions = ctx.getValidActions(state);
    const autoAction = actions.find((action) => action.autoExecute === true);
    if (autoAction) {
      state = executeAction(state, autoAction, ctx.rules, SIMULATION_OPTIONS);
      continue;
    }

    const playActions = actions.filter((action) => action.type === 'play');
    if (playActions.length === 0) {
      return state;
    }

    const selected = orderMovesForSearch(playActions, state, ctx, checkBudget)[0];
    if (!selected) {
      return state;
    }
    state = executeAction(state, selected, ctx.rules, SIMULATION_OPTIONS);
  }

  throw new Error('Monte Carlo rollout exceeded the maximum hand length');
}

/**
 * Quick evaluation: returns the best action without full evaluation data.
 * Use this when you just need the result, not the analysis.
 */
export function selectBestPlay(
  state: GameState,
  playActions: ValidAction[],
  myPlayerIndex: number,
  constraints: HandConstraints,
  ctx: ExecutionContext,
  config: MonteCarloConfig,
  searchOptions: ProfiledSearchOptions = {},
): ValidAction | null {
  if (playActions.length === 0) {
    return null;
  }

  if (playActions.length === 1) {
    return playActions[0] ?? null;
  }

  const { evaluations } = evaluatePlayActions(
    state,
    playActions,
    myPlayerIndex,
    constraints,
    ctx,
    config,
    searchOptions,
  );

  return evaluations[0]?.action ?? null;
}
